// TeacherOlympiadList.tsx
import React, {useEffect, useState} from 'react';
import {
    App,
    Button,
    Card,
    Col,
    InputNumber,
    Row,
    Select,
    Space,
    Tag,
    Typography,
    Tabs,
    Flex,
} from 'antd';
import {
    TeamOutlined,
    DownloadOutlined,
    SettingOutlined,
    EnvironmentOutlined,
} from '@ant-design/icons';
import {getRequest, getRequestWithParams} from '../../../../api/httpRequest';
import * as XLSX from 'xlsx';
import './TeacherOlympiadList.less';
import ParticipantsTable from './ParticipantsTable';
import OlympiadSettings from './settings/OlympiadSettings';
import PlacesManagement from './PlacesManagement';
import useBreakpoint from 'antd/es/grid/hooks/useBreakpoint';
import { userStore } from '../../../../store/store';

const {Title, Text} = Typography;
const {Option, OptGroup} = Select;
const {TabPane} = Tabs;

export interface FirstTourAnswer {
    taskId: string;
    taskNumber: string;
    correctAnswer: string;
    participantAnswer: string;
}

export interface Participant {
    id: number;
    fio: string;
    gender: string;
    birthDate: string;
    firstTourOption1: string;
    firstTourOption2: string;
    firstTourMaxSolved: string;
    admission: string;
    secondTourResult: string;
    secondTourStatus: string;
    email: string;
    phone: string;
    city: string;
    schoolName: string;
    schoolNumber: string;
    citizenship: string;
    region: string;
    registrationDate: string;
    activationDate: string;
    titlePagePrint: string;
    answerSheetPrint: string;
    country: string;
    institutionType: string;
    className: string;
    classProfile: string;
    firstTourPass: boolean;
    firstTourAnswers: FirstTourAnswer[];
    placeName: string;
}

interface Olympiad {
    id: number;
    name: string;
    subject: string;
    grade: string;
    period: string;
    discipline: string;
    dateEnd: string;
    tasks: any[];
    passingScore: string;
    secondRoundPassingScore: string;
}

const TeacherOlympiadList: React.FC = () => {
    const [olympiads, setOlympiads] = useState<Olympiad[]>([]);
    const [selectedOlympiad, setSelectedOlympiad] = useState<number | null>(null);
    const [selectedOlympiadData, setSelectedOlympiadData] = useState<Olympiad | null>(null);
    const [participants, setParticipants] = useState<Participant[]>([]);
    const [filteredParticipants, setFilteredParticipants] = useState<Participant[]>([]);
    const [loading, setLoading] = useState(true);
    const [loadingParticipants, setLoadingParticipants] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [passingScore, setPassingScore] = useState<number>(0);
    const [secondRoundPassingScore, setSecondRoundPassingScore] = useState<number>(0);
    const [activeTab, setActiveTab] = useState<string>('1');
    const {user, setUser} = userStore();
    const {notification} = App.useApp();
    const {xl} = useBreakpoint();

    useEffect(() => {
        loadOlympiads();
    }, []);

    const isCurator = () => {
        return user?.roles.includes('OLYMPIAD_CURATOR');
    };

    const loadOlympiads = () => {
        setLoading(true);
        getRequest('/server/olympiad/teacher/list')
            .then(response => {
                const formattedOlympiads = response.data.map((olympiad: any) => ({
                    id: olympiad.id,
                    name: `Олимпиада "Навигатор" (${olympiad.grade} класс)`,
                    subject: olympiad.discipline,
                    grade: olympiad.grade,
                    period: olympiad.period,
                    discipline: olympiad.discipline,
                    dateEnd: olympiad.dateEnd,
                    tasks: olympiad.tasks || [],
                    passingScore: olympiad.passingScore || '0',
                    secondRoundPassingScore: olympiad.secondRoundPassingScore
                }));
                setOlympiads(formattedOlympiads);
            })
            .catch(error => {
                notification.error({
                    message: 'Ошибка загрузки',
                    description: 'Не удалось загрузить список олимпиад'
                });
            })
            .finally(() => {
                setLoading(false);
            });
    };

    useEffect(() => {
        if (selectedOlympiad) {
            const olympiad = olympiads.find(o => o.id === selectedOlympiad);
            setSelectedOlympiadData(olympiad || null);

            if (olympiad) {
                setPassingScore(parseInt(olympiad.passingScore) || 0);
                setSecondRoundPassingScore(parseInt(olympiad.secondRoundPassingScore) || 0);
            }
            
            setLoadingParticipants(true);

            const requestParams: any = { olympiadId: selectedOlympiad };
            if (isCurator()) {
                requestParams.place = true;
            }

            getRequestWithParams(`/server/olympiad/participants`, requestParams)
                .then(response => {
                    const formattedParticipants = response.data.map((participant: any, index: number) => {
                        console.log(participant.secondTourResult)
                        return {
                            id: participant.id,
                            fio: participant.fio,
                            gender: participant.gender || 'Не указан',
                            birthDate: participant.birthDate || 'Не указана',
                            firstTourOption1: participant.firstTourOption1 || '0',
                            firstTourAnswers: participant.firstTourAnswers || [],
                            secondTourResult: participant.secondTourResult,
                            email: participant.email || 'Не указан',
                            phone: participant.phone || 'Не указан',
                            city: participant.city || 'Не указан',
                            school: participant.school || participant.institutionType || 'Не указано',
                            citizenship: participant.country || 'Не указано',
                            region: participant.region || 'Не указан',
                            registrationDate: participant.registrationDate || 'Не указана',
                            activationDate: participant.activationDate || 'Не активирован',
                            titlePagePrint: participant.titlePagePrint || '',
                            answerSheetPrint: participant.answerSheetPrint || '',
                            country: participant.country || 'Не указано',
                            institutionType: participant.institutionType || 'Не указано',
                            className: participant.className || 'Не указан',
                            classProfile: participant.classProfile || 'Не указан',
                            firstTourPass: participant.firstTourPass,
                            secondTourStatus: participant.secondTourStatus,
                            schoolName: participant.schoolName,
                            schoolNumber: participant.schoolNumber,
                            placeName: participant.placeName
                        };
                    });
                    console.log(formattedParticipants)
                    setParticipants(formattedParticipants);
                    setFilteredParticipants(formattedParticipants);
                })
                .catch(error => {
                    notification.error({
                        message: 'Ошибка загрузки',
                        description: 'Не удалось загрузить список участников'
                    });
                })
                .finally(() => {
                    setLoadingParticipants(false);
                });
        }
    }, [selectedOlympiad, olympiads]);

    useEffect(() => {
        if (searchText) {
            const filtered = participants.filter(participant =>
                participant.fio.toLowerCase().includes(searchText.toLowerCase()) ||
                participant.schoolName.toLowerCase().includes(searchText.toLowerCase()) ||
                participant.city.toLowerCase().includes(searchText.toLowerCase()) ||
                participant.institutionType.toLowerCase().includes(searchText.toLowerCase())
            );
            setFilteredParticipants(filtered);
        } else {
            setFilteredParticipants(participants);
        }
    }, [searchText, participants]);

    const handleOlympiadChange = (value: number) => {
        setSelectedOlympiad(value);
        setSearchText('');
        setActiveTab('3');
    };

    const loadParticipants = (olympiadId: number) => {
        setLoadingParticipants(true);
        const requestParams: any = { olympiadId };
        if (isCurator()) {
            requestParams.place = true;
        }

        getRequestWithParams(`/server/olympiad/participants`, requestParams)
            .then(response => {
                const formattedParticipants = response.data.map((participant: any, index: number) => {
                    console.log(participant.secondTourResult)
                    return {
                        id: participant.id,
                        fio: participant.fio,
                        gender: participant.gender || 'Не указан',
                        birthDate: participant.birthDate || 'Не указана',
                        firstTourOption1: participant.firstTourOption1 || '0',
                        firstTourAnswers: participant.firstTourAnswers || [],
                        secondTourResult: participant.secondTourResult,
                        secondTourStatus: participant.secondTourStatus,
                        email: participant.email || 'Не указан',
                        phone: participant.phone || 'Не указан',
                        city: participant.city || 'Не указан',
                        school: participant.school || participant.institutionType || 'Не указано',
                        citizenship: participant.country || 'Не указано',
                        region: participant.region || 'Не указан',
                        registrationDate: participant.registrationDate || 'Не указана',
                        activationDate: participant.activationDate || 'Не активирован',
                        titlePagePrint: participant.titlePagePrint || '',
                        answerSheetPrint: participant.answerSheetPrint || '',
                        country: participant.country || 'Не указано',
                        institutionType: participant.institutionType || 'Не указано',
                        className: participant.className || 'Не указан',
                        classProfile: participant.classProfile || 'Не указан',
                        firstTourPass: participant.firstTourPass,
                        schoolName: participant.schoolName,
                        schoolNumber: participant.schoolNumber
                    };
                });
                console.log(formattedParticipants)
                setParticipants(formattedParticipants);
                setFilteredParticipants(formattedParticipants);
            })
            .catch(error => {
                notification.error({
                    message: 'Ошибка загрузки',
                    description: 'Не удалось загрузить список участников'
                });
            })
            .finally(() => {
                setLoadingParticipants(false);
            });
    };

    const handlePassingScoreChange = (value: number | null) => {
        setPassingScore(value || 0);
    };

    const handlePassingScoreUpdate = (newPassingScore: number) => {
        setPassingScore(newPassingScore);
    };

    const exportToExcel = () => {
        if (filteredParticipants.length === 0) {
            notification.warning({
                message: 'Нет данных',
                description: 'Нет данных для выгрузки'
            });
            return;
        }

        try {
            const excelData = filteredParticipants.map(participant => ({
                '№': participant.id,
                'ФИО': participant.fio,
                'Пол': participant.gender,
                'Дата рождения': participant.birthDate,
                'Первый тур': participant.firstTourOption1,
                'Допуск': participant.firstTourPass,
                'Второй тур': participant.secondTourResult,
                'Статус во втором туре': participant.secondTourStatus,
                'Email': participant.email,
                'Телефон': participant.phone,
                'Город проведения': participant.city,
                'Площадка': participant.placeName,
                'Учебное заведение': participant.schoolName,
                'Номер школы': participant.schoolNumber,
                'Тип учреждения': participant.institutionType,
                'Класс': participant.className,
                'Профиль класса': participant.classProfile,
                'Гражданство': participant.citizenship,
                'Регион': participant.region,
                'Дата записи': participant.registrationDate
            }));

            const ws = XLSX.utils.json_to_sheet(excelData);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Участники олимпиады');

            const selectedOlympiadData = olympiads.find(o => o.id === selectedOlympiad);
            const fileName = selectedOlympiadData 
                ? `Участники_${selectedOlympiadData.subject}_${selectedOlympiadData.period}.xlsx`
                : 'Участники_олимпиады.xlsx';

            XLSX.writeFile(wb, fileName);

            notification.success({
                message: 'Выгрузка завершена',
                description: `Файл ${fileName} успешно сохранен`
            });
        } catch (error) {
            notification.error({
                message: 'Ошибка выгрузки',
                description: 'Не удалось выгрузить данные в Excel'
            });
        }
    };

    const olympiadOptions = [
        <OptGroup key="9-10" label="9-10 классы">
            {olympiads
                .filter(o => o.grade === '9-10')
                .map(o => (
                    <Option key={o.id} value={o.id}>
                        {o.subject} ({o.period})
                    </Option>
                ))}
        </OptGroup>,
        <OptGroup key="11" label="11 класс">
            {olympiads
                .filter(o => o.grade === '11')
                .map(o => (
                    <Option key={o.id} value={o.id}>
                        {o.subject} ({o.period})
                    </Option>
                ))}
        </OptGroup>
    ];

    return (
        <div className="teacher-olympiad-page">
            <div className="teacher-olympiad-container">

                <Card className="selection-card" loading={loading}>
                    <Title level={4} className="year-title">2025/2026</Title>

                    <div className="select-info-text">
                        Пожалуйста, выберите интересующую Вас олимпиаду
                    </div>

                    <Row gutter={[16, 16]} align="middle">
                        <Col xs={24}>
                            <Select
                                placeholder="Выберите олимпиаду"
                                onChange={handleOlympiadChange}
                                style={{width: '100%'}}
                                value={selectedOlympiad}
                                allowClear
                            >
                                {olympiadOptions}
                            </Select>
                        </Col>
                    </Row>
                </Card>

                {selectedOlympiad && selectedOlympiadData && (
                    <Card style={{marginTop: 16}}>
                        <Tabs 
                            activeKey={activeTab} 
                            onChange={setActiveTab}
                            type="card"
                        >
                            {!isCurator() && (
                                <TabPane
                                    style={{paddingTop: 16}}
                                    tab={
                                        <span>
                                            <EnvironmentOutlined />
                                            Настройка площадок
                                        </span>
                                    }
                                    key="1"
                                >
                                    <PlacesManagement olympiadId={selectedOlympiad} />
                                </TabPane>
                            )}

                            {!isCurator() && (
                                <TabPane
                                    tab={
                                        <span>
                                            <SettingOutlined />
                                            Настройки олимпиады
                                        </span>
                                    }
                                    key="2"
                                >
                                    <OlympiadSettings
                                        olympiadData={selectedOlympiadData}
                                        passingScore={passingScore}
                                        onPassingScoreChange={handlePassingScoreUpdate}
                                    />
                                </TabPane>
                            )}

                            <TabPane 
                                tab={
                                    <span>
                                        <TeamOutlined />
                                        Список участников
                                        {filteredParticipants.length > 0 && (
                                            <Tag color="blue" style={{marginLeft: 8}}>
                                                {filteredParticipants.length}
                                            </Tag>
                                        )}
                                    </span>
                                } 
                                key="3"
                            >
                                <Flex vertical gap={8}>
                                    <Row gutter={[16, 16]} style={{marginTop: 16}}>
                                        <Col xs={24} sm={12} md={6}>
                                            <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
                                                <Text strong>Проходной балл:</Text>
                                                <InputNumber
                                                    min={0}
                                                    max={100}
                                                    value={passingScore}
                                                    onChange={handlePassingScoreChange}
                                                    style={{width: 80}}
                                                    disabled={isCurator()}
                                                />
                                            </div>
                                        </Col>
                                        <Col xs={24} sm={12} md={8}>
                                            <Button
                                                type="primary"
                                                icon={<DownloadOutlined/>}
                                                onClick={exportToExcel}
                                                disabled={filteredParticipants.length === 0}
                                            >
                                                Экспорт в Excel
                                            </Button>
                                        </Col>
                                    </Row>

                                    <ParticipantsTable
                                        participants={filteredParticipants}
                                        loading={loadingParticipants}
                                        passingScore={passingScore}
                                        maxScore={secondRoundPassingScore}
                                        olympiadId={selectedOlympiad}
                                        isCurator={isCurator()}
                                        onDataUpdate={() => {
                                            console.log('update')
                                            if (selectedOlympiad) {
                                                loadParticipants(selectedOlympiad);
                                            }
                                        }}
                                    />
                                </Flex>
                            </TabPane>
                        </Tabs>
                    </Card>
                )}
            </div>
        </div>
    );
};

export default TeacherOlympiadList;